/**
 * Nested cart line (Shopify parent_line_key): after the theme adds the main product via POST
 * /cart/add, chain-add selected protection plan(s) as child line(s) using `parent_line_key` from the
 * first response — same pattern as `prosuro-js-dev-nested.js`, but driven by `prosuro:plans:change`.
 *
 * Prerequisites (non-RX branch of nested-test.liquid):
 *   - Any widget-runtime PDP bundle paired with this block (compact, generic, brand-specific) fires
 *     `prosuro:plans:change` with `detail.plans` reactively as the buyer picks a tier — the generic
 *     sales modal only fires it when "Add Protection" is clicked inside the modal, not on open/pick.
 *
 * Mirrors selection into `window.__prosuroSelectedPlans` (same shape as `prosuro-cart-add-unnested.js`).
 * Child line properties align with unnested plan rows: `_Term`, `_TierPrice`, `_SKU`, `_Pg`, `_Pt: 'p'`.
 *
 * Integration:
 *   1) `window.fetch` — PATCHES global `fetch`. If the theme bundle captured `fetch` in a closure
 *      before this file runs, those calls never hit the patch (see Diagnose). The reverse also
 *      happens on storefronts with a lot of third-party scripts: something loads AFTER this file
 *      and reassigns `window.fetch` outright (rather than wrapping the existing one), silently
 *      discarding our patch — `window.fetch.__prosuroNestedCartFetchPatch` goes missing even though
 *      `window.__prosuroNestedCartModuleLoaded` stays true. `ensureFetchPatchInstalled()` guards
 *      against this: it reasserts our wrapper as the outermost `window.fetch` on a capture-phase
 *      document click listener (so it's fresh by the time an Add to Cart click's own handler runs)
 *      plus a low-frequency backstop poll, rather than patching once at load and trusting it stays.
 *   2) `Shopify.addItemFromForm` — we POST the form via the native fetch reference and chain.
 *   3) `XMLHttpRequest` — PATCHES `.open`/`.send` the same way, mirroring the fetch patch's logic.
 *      Confirmed by field-testing (STEPR/getstepr.com): some themes' product-form JS (jQuery
 *      `$.ajax()` in that case) sends the actual `/cart/add.js` POST via XHR, never touching
 *      `fetch` at all. On those themes the fetch patch can be perfectly healthy and this file will
 *      still never see the request — this isn't an edge-case fallback, it's required. Same
 *      self-healing pattern as the fetch patch (`ensureXhrPatchInstalled()`, reasserted on click +
 *      backstop poll), since prototype methods can get re-wrapped by other scripts too.
 *
 * Cart drawer redraw + open: the chain POST requests `sections` (via
 * `window.ProsuroCartDrawerSync.buildSectionsList`) and, once it resolves, hands the response to
 * `window.ProsuroCartDrawerSync.refreshAndOpenCart` — which swaps the section HTML into the DOM and
 * opens the drawer (preferring `Shopify.actions.openCart()` on themes that support it). Load
 * `prosuro-cart-drawer-sync.js` BEFORE this file. Without it, the plan line still gets added
 * server-side, but nothing here shows it.
 *
 * Diagnose (after each successful POST /cart/add.*):
 *   `window.__prosuroNestedCartDiag` = { chained, skipReasons, plansLen, url, at }
 *   Also verify a plan is selected: `window.__prosuroSelectedPlans` (must be non-empty) or listen for
 *   `prosuro:plans:change` on document with capture.
 *
 * Silence logs: `window.__PROSURO_NESTED_CART_DEBUG__ = false`
 *
 * Does not stack with `prosuro-cart-add-unnested.js` (use one or the other per template).
 */
(function () {
  'use strict';

  if (window.__prosuroNestedCartModuleLoaded) return;
  if (typeof window.fetch !== 'function') return;
  window.__prosuroNestedCartModuleLoaded = true;

  /** ~48 bits of randomness; shared across child lines in one chain request. */
  function prosuroBundleId() {
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
      var buf = new Uint8Array(6);
      crypto.getRandomValues(buf);
      var hex = '';
      for (var i = 0; i < buf.length; i += 1) {
        var h = buf[i].toString(16);
        hex += h.length < 2 ? '0' + h : h;
      }
      return hex;
    }
    return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
  }

  window.__prosuroSelectedPlans = window.__prosuroSelectedPlans || [];

  document.addEventListener(
    'prosuro:plans:change',
    function (e) {
      var raw = (e.detail && e.detail.plans) || [];
      window.__prosuroSelectedPlans = raw.map(function (p) {
        return {
          id: p.planVariantId,
          term: String(p.term),
          tierPrice: p.tierPriceDollars,
          sku: p.sku,
        };
      });
    },
    true,
  );

  var prosuroNestedChainFetch = false;
  var origFetch = window.fetch.bind(window);

  try {
    window.__prosuroNestedCartNativeFetch = origFetch;
  } catch (_) {}

  function debugEnabled() {
    return window.__PROSURO_NESTED_CART_DEBUG__ !== false;
  }

  function log() {
    if (!debugEnabled()) return;
    var a = Array.prototype.slice.call(arguments);
    a.unshift('[Prosuro nested cart]');
    console.log.apply(console, a);
  }

  function logWarn() {
    if (!debugEnabled()) return;
    var a = Array.prototype.slice.call(arguments);
    a.unshift('[Prosuro nested cart]');
    console.warn.apply(console, a);
  }

  function setNestedCartDiag(o) {
    try {
      window.__prosuroNestedCartDiag = o;
    } catch (_) {}
  }

  function getRequestUrl(input) {
    if (typeof input === 'string') return input;
    if (input && typeof input.url === 'string') return input.url;
    return '';
  }

  function isCartAddUrl(url) {
    return typeof url === 'string' && url.indexOf('cart/add') !== -1;
  }

  function getCartAddJsUrl() {
    if (
      typeof window.Shopify !== 'undefined' &&
      window.Shopify.routes &&
      typeof window.Shopify.routes.root === 'string'
    ) {
      return window.Shopify.routes.root + 'cart/add.js';
    }
    return '/cart/add.js';
  }

  function getCartJsUrl() {
    if (
      typeof window.Shopify !== 'undefined' &&
      window.Shopify.routes &&
      typeof window.Shopify.routes.root === 'string'
    ) {
      return window.Shopify.routes.root + 'cart.js';
    }
    return '/cart.js';
  }

  /** Cart line items often have `variant_id`; some responses only expose variant as `id`. */
  function lineItemVariantId(item) {
    if (!item || typeof item !== 'object') return NaN;
    if (item.variant_id != null && item.variant_id !== '') return Number(item.variant_id);
    if (item.id != null && item.id !== '') return Number(item.id);
    return NaN;
  }

  function selectedPlanVariantIdSet() {
    var plans = window.__prosuroSelectedPlans || [];
    var set = {};
    for (var i = 0; i < plans.length; i++) {
      var id = plans[i] && plans[i].id;
      if (id != null && id !== '') set[String(Number(id))] = true;
    }
    return set;
  }

  function requestAlreadyIncludesPlanVariants(init, idSet) {
    if (!init || typeof init.body !== 'string') return false;
    try {
      var j = JSON.parse(init.body);
      var items = j.items || [];
      for (var i = 0; i < items.length; i++) {
        var vid = items[i] && items[i].id;
        if (vid != null && idSet[String(Number(vid))]) return true;
      }
    } catch (_) {}
    return false;
  }

  function cartAddResponseItemList(data) {
    if (!data || typeof data !== 'object') return [];
    var items = data.items;
    if (Array.isArray(items) && items.length > 0) return items;
    if (data.item && typeof data.item === 'object') return [data.item];
    /*
     * Some themes / responses return one line item at the root (no `items` array).
     * Do not treat a full cart object as a line: cart payloads use `token`.
     */
    if (
      data.token == null &&
      data.items === undefined &&
      (data.variant_id != null || data.id != null)
    ) {
      return [data];
    }
    return [];
  }

  function responseIncludesSelectedPlanVariants(data, idSet) {
    var list = cartAddResponseItemList(data);
    for (var i = 0; i < list.length; i++) {
      var n = lineItemVariantId(list[i]);
      if (!Number.isFinite(n)) continue;
      if (idSet[String(n)]) return true;
    }
    return false;
  }

  function findParentLineKeyFromCartAddResponse(data, mainVariantIdStr) {
    var vid = Number(mainVariantIdStr);
    var list = cartAddResponseItemList(data);
    if (list.length === 0) return null;

    for (var i = 0; i < list.length; i++) {
      var item = list[i];
      if (item && item.key && lineItemVariantId(item) === vid) return String(item.key);
    }

    if (list.length === 1 && list[0].key) return String(list[0].key);

    for (var j = 0; j < list.length; j++) {
      if (list[j] && list[j].key) return String(list[j].key);
    }
    return null;
  }

  function findParentKeyInCartJsPayload(cart, mainVariantIdStr) {
    var vid = Number(mainVariantIdStr);
    var cartItems = cart && cart.items;
    if (!Array.isArray(cartItems)) return null;
    for (var i = cartItems.length - 1; i >= 0; i--) {
      var it = cartItems[i];
      if (!it || !it.key) continue;
      if (lineItemVariantId(it) === vid) return String(it.key);
    }
    return null;
  }

  function fetchParentLineKeyFromCartJs(mainVariantIdStr) {
    return origFetch(getCartJsUrl(), { credentials: 'same-origin' })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        return findParentKeyInCartJsPayload(cart, mainVariantIdStr);
      })
      .catch(function (err) {
        logWarn('/cart.js fallback failed', err);
        return null;
      });
  }

  /** Variant id from POST body when provided on `init` (FormData / JSON string). */
  function getMainVariantIdFromPostBody(init) {
    if (!init || !Object.prototype.hasOwnProperty.call(init, 'body')) return null;
    var body = init.body;
    if (body == null || body === undefined) return null;
    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      var id = body.get('id');
      return id ? String(id) : null;
    }
    if (typeof body === 'string') {
      try {
        var j = JSON.parse(body);
        if (Array.isArray(j.items) && j.items[0] && j.items[0].id != null) {
          return String(j.items[0].id);
        }
        if (j.id != null) return String(j.id);
      } catch (_) {
        return null;
      }
    }
    return null;
  }

  /**
   * Prefer request body when readable; else use /cart/add JSON (handles fetch(Request) w/ stream body).
   */
  function inferMainVariantIdFromCartAdd(data, init, idSet) {
    var fromBody = getMainVariantIdFromPostBody(init);
    if (fromBody && !idSet[fromBody]) return fromBody;
    var list = cartAddResponseItemList(data);
    for (var i = 0; i < list.length; i++) {
      var n = lineItemVariantId(list[i]);
      if (!Number.isFinite(n)) continue;
      var vs = String(n);
      if (!idSet[vs]) return vs;
    }
    if (list.length === 1) {
      var lone = lineItemVariantId(list[0]);
      if (Number.isFinite(lone)) return String(lone);
    }
    return null;
  }

  function buildChainedItems(parentLineKey, plans) {
    var pg = prosuroBundleId();
    var out = [];
    for (var i = 0; i < plans.length; i++) {
      var p = plans[i];
      if (!p || p.id == null || p.id === '') continue;
      out.push({
        id: Number(p.id),
        quantity: 1,
        parent_line_key: parentLineKey,
        properties: {
          _Term: String(p.term),
          _TierPrice: p.tierPrice,
          _SKU: String(p.sku),
          _Pg: pg,
          _Pt: 'p',
        },
      });
    }
    return out;
  }

  function runPlanChainFromCartAddJson(data, mainVariantIdStr, plans) {
    // Consume the selection the moment we commit to chaining it — not after the chain resolves.
    // window.fetch/XHR are patched globally, watching ANY POST to a *cart/add URL on the page, from
    // any script, not just our own widget's submission. evaluateChainAfterCartAdd's guards check
    // things like "does the response already include our plan variant" or "is the new variant one
    // of our plan ids" — none of them confirm the observed /cart/add actually came from our own PDP
    // flow. Without clearing this, a stale (already-successfully-chained) selection stays sitting in
    // window.__prosuroSelectedPlans for the rest of the page session and can get chained a SECOND
    // time onto a completely unrelated later /cart/add — confirmed field-testing on STEPR: Route's
    // own checkout-time /cart/add.js for its shipping-protection line got caught by this exact gap,
    // resulting in a second Guardsman plan line parented to Route's line instead of the product.
    if (window.__prosuroSelectedPlans && window.__prosuroSelectedPlans.length) {
      window.__prosuroSelectedPlans = [];
    }

    var fromAdd = findParentLineKeyFromCartAddResponse(data, mainVariantIdStr);
    var keyPromise = fromAdd
      ? Promise.resolve(fromAdd)
      : fetchParentLineKeyFromCartJs(mainVariantIdStr);

    return keyPromise.then(function (parentLineKey) {
      log('parent line key', {
        parentLineKey: parentLineKey,
        mainVariantId: mainVariantIdStr,
        planCount: plans.length,
        source: fromAdd ? 'cart/add response' : 'GET /cart.js fallback',
      });
      if (!parentLineKey) {
        logWarn(
          'parent line key is null — cannot chain (check cart/add JSON shape and /cart.js line.key for variant',
          mainVariantIdStr,
          ')',
        );
        return;
      }
      var items = buildChainedItems(parentLineKey, plans);
      if (!items.length) return;
      prosuroNestedChainFetch = true;
      var chainUrl = getCartAddJsUrl();
      var drawerSync = window.ProsuroCartDrawerSync;
      var cartContext = drawerSync && typeof drawerSync.getCartContext === 'function'
        ? drawerSync.getCartContext()
        : null;
      var sectionsList = drawerSync && typeof drawerSync.buildSectionsList === 'function'
        ? drawerSync.buildSectionsList(cartContext)
        : [];
      var chainBody = { items: items };
      if (sectionsList.length) {
        chainBody.sections = sectionsList.join(',');
      }
      log('chain POST', chainUrl, chainBody);
      return origFetch(chainUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(chainBody),
      })
        .then(function (chainRes) {
          log('chain response', { ok: chainRes.ok, status: chainRes.status });
          if (!chainRes.ok) {
            return chainRes.text().then(function (t) {
              logWarn('chain error body', (t || '').slice(0, 500));
            });
          }
          return chainRes.json().then(function (chainData) {
            if (drawerSync && typeof drawerSync.refreshAndOpenCart === 'function') {
              try {
                drawerSync.refreshAndOpenCart({
                  sections: chainData && chainData.sections,
                  lines: items,
                  cart: chainData,
                  parentLineKey: parentLineKey,
                });
              } catch (e) {
                logWarn('drawer sync failed', e);
              }
            } else {
              logWarn('window.ProsuroCartDrawerSync not found — load prosuro-cart-drawer-sync.js before this file');
            }
          }).catch(function (err) {
            logWarn('chain response not JSON; cannot redraw/open drawer', err);
          });
        })
        .catch(function (err) {
          logWarn('chain fetch failed', err);
        })
        .finally(function () {
          prosuroNestedChainFetch = false;
        });
    });
  }

  function evaluateChainAfterCartAdd(data, init) {
    var reasons = [];
    if (prosuroNestedChainFetch) {
      reasons.push('internal chain in progress');
      return { chain: false, mainId: null, reasons: reasons };
    }
    var plans = window.__prosuroSelectedPlans || [];
    if (!plans.length) {
      reasons.push('no plans in window.__prosuroSelectedPlans');
      return { chain: false, mainId: null, reasons: reasons };
    }
    var idSet = selectedPlanVariantIdSet();
    if (responseIncludesSelectedPlanVariants(data, idSet)) {
      reasons.push('response already includes selected plan variant line(s)');
      return { chain: false, mainId: null, reasons: reasons };
    }
    if (requestAlreadyIncludesPlanVariants(init, idSet)) {
      reasons.push('request JSON body already includes plan variant(s)');
      return { chain: false, mainId: null, reasons: reasons };
    }
    var mainId = inferMainVariantIdFromCartAdd(data, init, idSet);
    if (!mainId) {
      reasons.push('could not infer main variant id (body unreadable or ambiguous response)');
      return { chain: false, mainId: null, reasons: reasons };
    }
    for (var i = 0; i < plans.length; i++) {
      if (Number(mainId) === Number(plans[i].id)) {
        reasons.push('main variant id equals a plan variant id');
        return { chain: false, mainId: null, reasons: reasons };
      }
    }
    return { chain: true, mainId: mainId, reasons: reasons };
  }

  function prosuroFetchWrapper(input, init) {
    if (prosuroNestedChainFetch) {
      return origFetch(input, init);
    }

    var reqUrl = getRequestUrl(input);
    var method =
      (init && init.method) ||
      (typeof input === 'object' && input && input.method) ||
      'GET';
    var isPost = String(method).toUpperCase() === 'POST';

    var result = origFetch(input, init);

    if (!isPost || !isCartAddUrl(reqUrl)) {
      return result;
    }

    log('POST cart/add observed (this line means the global fetch patch saw the request)', {
      url: reqUrl,
    });

    return result.then(function (response) {
      if (!response.ok) {
        setNestedCartDiag({
          chained: false,
          skipReasons: ['response not ok'],
          plansLen: (window.__prosuroSelectedPlans || []).length,
          url: reqUrl,
          at: Date.now(),
        });
        return response;
      }

      return response
        .clone()
        .json()
        .then(function (data) {
          var ev = evaluateChainAfterCartAdd(data, init);
          setNestedCartDiag({
            chained: ev.chain,
            skipReasons: ev.chain ? [] : ev.reasons,
            plansLen: (window.__prosuroSelectedPlans || []).length,
            url: reqUrl,
            at: Date.now(),
            mainVariantIfEligible: ev.mainId || undefined,
          });
          if (!ev.chain) {
            log('skip chain:', ev.reasons.join(' | '));
            return response;
          }
          var plans = window.__prosuroSelectedPlans || [];
          return runPlanChainFromCartAddJson(data, ev.mainId, plans).then(function () {
            setNestedCartDiag({
              chained: true,
              skipReasons: [],
              plansLen: plans.length,
              url: reqUrl,
              at: Date.now(),
              mainVariantIfEligible: ev.mainId,
            });
            return response;
          });
        })
        .catch(function (err) {
          logWarn('cart/add response not JSON; cannot chain', err);
          setNestedCartDiag({
            chained: false,
            skipReasons: ['response JSON parse failed'],
            plansLen: (window.__prosuroSelectedPlans || []).length,
            url: reqUrl,
            at: Date.now(),
            parseError: String(err && err.message ? err.message : err),
          });
          return response;
        });
    });
  }

  /**
   * Re-asserts `window.fetch === prosuroFetchWrapper`. Safe/cheap to call repeatedly — a no-op
   * whenever our marker is still present. Called at load, on every capture-phase document click
   * (so it's fresh right before whatever handler an Add to Cart click triggers), and on a
   * low-frequency backstop poll for mutations that don't originate from a click.
   */
  function ensureFetchPatchInstalled() {
    if (typeof window.fetch !== 'function') return;
    if (window.fetch.__prosuroNestedCartFetchPatch === true) return;
    window.fetch = prosuroFetchWrapper;
    window.fetch.__prosuroNestedCartFetchPatch = true;
    log('fetch patch (re)installed — plans from prosuro:plans:change; chain uses parent_line_key');
  }

  ensureFetchPatchInstalled();
  document.addEventListener('click', ensureFetchPatchInstalled, true);
  setInterval(ensureFetchPatchInstalled, 1500);

  // ---------- XMLHttpRequest patch — some themes' product-form JS (jQuery $.ajax(), etc.) uses XHR
  // instead of fetch for cart/add. Confirmed field-testing: fetch never sees the request at all on
  // those themes, only XHR does — this is not a fallback for edge cases, it's required on themes
  // where fetch patching alone silently does nothing, however healthy the fetch patch itself is. ----

  var origXhrOpen = XMLHttpRequest.prototype.open;
  var origXhrSend = XMLHttpRequest.prototype.send;

  function prosuroXhrOpen(method, url) {
    try {
      this.__prosuroIsCartAdd = String(method).toUpperCase() === 'POST' && isCartAddUrl(String(url));
      this.__prosuroReqUrl = String(url);
    } catch (_) {}
    return origXhrOpen.apply(this, arguments);
  }

  function prosuroXhrSend(body) {
    if (this.__prosuroIsCartAdd && !prosuroNestedChainFetch) {
      var xhr = this;
      var reqUrl = xhr.__prosuroReqUrl;
      xhr.addEventListener('load', function () {
        if (xhr.status < 200 || xhr.status >= 300) {
          setNestedCartDiag({
            chained: false,
            skipReasons: ['response not ok'],
            plansLen: (window.__prosuroSelectedPlans || []).length,
            url: reqUrl,
            at: Date.now(),
          });
          return;
        }

        var data;
        try {
          data = JSON.parse(xhr.responseText || '{}');
        } catch (err) {
          logWarn('XHR cart/add response not JSON; cannot chain', err);
          setNestedCartDiag({
            chained: false,
            skipReasons: ['response JSON parse failed'],
            plansLen: (window.__prosuroSelectedPlans || []).length,
            url: reqUrl,
            at: Date.now(),
            parseError: String(err && err.message ? err.message : err),
          });
          return;
        }

        log('XHR POST cart/add observed (this line means the XHR patch saw the request)', { url: reqUrl });

        // `getMainVariantIdFromPostBody`/`requestAlreadyIncludesPlanVariants` expect a fetch-style
        // `init` object. jQuery typically sends a url-encoded string body (not JSON, not FormData),
        // so `init.body` parsing there falls through harmlessly and `inferMainVariantIdFromCartAdd`
        // still resolves the main variant id from the /cart/add.js JSON response instead — no
        // special-casing needed here for the request body shape.
        var init = { body: body };
        var ev = evaluateChainAfterCartAdd(data, init);
        setNestedCartDiag({
          chained: ev.chain,
          skipReasons: ev.chain ? [] : ev.reasons,
          plansLen: (window.__prosuroSelectedPlans || []).length,
          url: reqUrl,
          at: Date.now(),
          mainVariantIfEligible: ev.mainId || undefined,
        });
        if (!ev.chain) {
          log('skip chain (XHR):', ev.reasons.join(' | '));
          return;
        }
        var plans = window.__prosuroSelectedPlans || [];
        runPlanChainFromCartAddJson(data, ev.mainId, plans).then(function () {
          setNestedCartDiag({
            chained: true,
            skipReasons: [],
            plansLen: plans.length,
            url: reqUrl,
            at: Date.now(),
            mainVariantIfEligible: ev.mainId,
          });
        });
      });
    }
    return origXhrSend.apply(this, arguments);
  }

  function ensureXhrPatchInstalled() {
    if (
      XMLHttpRequest.prototype.open.__prosuroNestedCartXhrPatch === true &&
      XMLHttpRequest.prototype.send.__prosuroNestedCartXhrPatch === true
    ) {
      return;
    }
    XMLHttpRequest.prototype.open = prosuroXhrOpen;
    XMLHttpRequest.prototype.send = prosuroXhrSend;
    XMLHttpRequest.prototype.open.__prosuroNestedCartXhrPatch = true;
    XMLHttpRequest.prototype.send.__prosuroNestedCartXhrPatch = true;
    log('XHR patch (re)installed — some themes use $.ajax()/XHR for cart/add instead of fetch');
  }

  ensureXhrPatchInstalled();
  document.addEventListener('click', ensureXhrPatchInstalled, true);
  setInterval(ensureXhrPatchInstalled, 1500);

  // ---------- Theme "redirect to /cart page" override, when present -------------------------------
  //
  // Some themes decide "open drawer" vs. "hard-redirect to /cart" after a successful add by reading
  // a plain settings flag fresh at add-time (not captured/cached at page load) — confirmed on
  // STEPR's theme (`theme.js`): its `theme.addedToCartHandler` reads
  // `theme.settings.after_add_to_cart` ('page' | 'drawer') and does
  // `window.location = theme.routes.cart_url` for 'page'. A hard navigation right after a
  // successful chain-add discards whatever this file/`prosuro-cart-drawer-sync.js` just did — there
  // is no reliable way to cancel a script-initiated `location` navigation from outside the code that
  // triggers it (see header comment), so this has to be prevented before it's decided, not after.
  //
  // Forcing this flag to 'drawer' (only when found already set to 'page' — never invents the
  // property) lets the theme's OWN native, already-correct drawer-open path run instead of us
  // reimplementing it: its 'drawer' branch dispatches `theme:cartchanged` with
  // `detail: { openDrawer: true }` for its own cart-drawer component to handle. Scoped to pages that
  // load this script, not a merchant-wide config change — the theme customizer's own
  // "after adding a product" setting is the right place for a store-wide version of this choice.

  function ensureThemeDrawerModeForced() {
    try {
      if (window.theme && window.theme.settings && window.theme.settings.after_add_to_cart === 'page') {
        window.theme.settings.after_add_to_cart = 'drawer';
        log('forced theme.settings.after_add_to_cart "page" -> "drawer" to avoid a hard /cart redirect after chain-add');
      }
    } catch (_) {}
  }

  ensureThemeDrawerModeForced();
  document.addEventListener('click', ensureThemeDrawerModeForced, true);
  setInterval(ensureThemeDrawerModeForced, 1500);

  /** Avoid `form.id` when a control is named "id"; supports jQuery-wrapped form. */
  function normalizeFormElement(form) {
    if (!form) return null;
    var tag = form.tagName && typeof form.tagName === 'string' ? String(form.tagName).toLowerCase() : '';
    if (form.nodeType === 1 && tag === 'form') return form;
    if (form[0] && form[0].nodeType === 1) {
      var t = form[0].tagName && String(form[0].tagName).toLowerCase();
      if (t === 'form') return form[0];
    }
    return null;
  }

  function mainVariantIdFromFormElement(formEl) {
    try {
      var fd = new FormData(formEl);
      var id = fd.get('id');
      if (id == null || id === '') return null;
      return String(id);
    } catch (_) {
      return null;
    }
  }

  function shouldNestAddItemFromForm(formEl, plans, reasons) {
    if (!plans.length) {
      reasons.push('no plans selected');
      return false;
    }
    if (!formEl) {
      reasons.push('not a form element');
      return false;
    }
    var mainId = mainVariantIdFromFormElement(formEl);
    if (!mainId) {
      reasons.push('FormData missing id');
      return false;
    }
    for (var i = 0; i < plans.length; i++) {
      if (Number(mainId) === Number(plans[i].id)) {
        reasons.push('main variant id equals a plan variant id');
        return false;
      }
    }
    return true;
  }

  function mountShopifyAddItemFromFormPatch() {
    if (typeof window.Shopify === 'undefined' || typeof window.Shopify.addItemFromForm !== 'function') {
      return false;
    }
    if (window.Shopify.addItemFromForm.__prosuroNestedAddItemPatch) {
      return true;
    }
    if (window.Shopify.addItemFromForm.__prosuroUnnestedPatch) {
      log('Shopify.addItemFromForm already has unnested patch — skipping nested addItem wrapper');
      return true;
    }

    var previous = window.Shopify.addItemFromForm;

    window.Shopify.addItemFromForm = function (form, success, error, evt) {
      var plans = window.__prosuroSelectedPlans || [];
      var formEl = normalizeFormElement(form);
      var reasons = [];
      if (!shouldNestAddItemFromForm(formEl, plans, reasons)) {
        return previous.apply(Shopify, arguments);
      }

      if (evt && typeof evt.preventDefault === 'function') {
        evt.preventDefault();
      }

      var mainId = mainVariantIdFromFormElement(formEl);
      log('Shopify.addItemFromForm: main POST + plan chain (legacy cart path)', {
        mainVariantId: mainId,
        planCount: plans.length,
      });

      var fd = new FormData(formEl);

      return origFetch(getCartAddJsUrl(), {
        method: 'POST',
        body: fd,
        credentials: 'same-origin',
      })
        .then(function (res) {
          return res.text().then(function (text) {
            var data = null;
            try {
              data = text ? JSON.parse(text) : null;
            } catch (_) {}
            return { res: res, data: data, text: text };
          });
        })
        .then(function (out) {
          if (!out.res.ok) {
            logWarn('main cart/add failed', out.res.status, (out.text || '').slice(0, 300));
            if (typeof error === 'function') {
              error(out.data || out.text);
            }
            return;
          }
          return runPlanChainFromCartAddJson(out.data, mainId, plans).then(function () {
            if (typeof success === 'function') {
              success(out.data, formEl);
            } else {
              window.location.href =
                typeof window.Shopify !== 'undefined' &&
                window.Shopify.routes &&
                typeof window.Shopify.routes.root === 'string'
                  ? window.Shopify.routes.root + 'cart'
                  : '/cart';
            }
          });
        })
        .catch(function (err) {
          logWarn('addItemFromForm nested path failed', err);
          if (typeof error === 'function') {
            error(err);
          } else {
            console.error(err);
          }
        });
    };

    window.Shopify.addItemFromForm.__prosuroNestedAddItemPatch = true;
    log('Shopify.addItemFromForm patch installed (Quay / legacy themes that omit fetch for cart/add)');
    return true;
  }

  if (!mountShopifyAddItemFromFormPatch()) {
    var addItemAttempts = 0;
    var addItemMax = 200;
    var addItemTimer = setInterval(function () {
      addItemAttempts += 1;
      var addItemOk = mountShopifyAddItemFromFormPatch();
      if (addItemOk || addItemAttempts >= addItemMax) {
        clearInterval(addItemTimer);
        if (!addItemOk && addItemAttempts >= addItemMax) {
          logWarn(
            'Shopify.addItemFromForm not available after ' +
              addItemMax +
              ' retries — legacy cart hook skipped (fetch patch still active).',
          );
        }
      }
    }, 50);
  }
})();
